import React, { useEffect, useRef, lazy, Component } from 'react';
import { PaletteIcon, PencilRulerIcon, LayoutTemplateIcon, MousePointer2Icon, ComponentIcon, GlobeIcon, ZapIcon, FlaskConicalIcon, SmartphoneIcon, ShoppingBagIcon, BotIcon, SearchIcon, MapIcon, CodeIcon, ServerIcon, CheckCircleIcon, HeadphonesIcon, MonitorIcon, PackageIcon } from 'lucide-react';
function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => el.classList.add('in-view'), delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.08
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
// ── Logo color: warm brown tint matching hero background ──────────────────
const LOGO_COLOR = '#ABABAB';
const LOGOS = [{
  name: 'Narra',
  svg: <img src="https://cdn.magicpatterns.com/uploads/wfhGfqyhPgSM9ub1eNcUs2/Group_4.svg" alt="Narra" style={{
    height: 28,
    width: 'auto',
    display: 'block'
  }} data-id="element-119" data-mp="1x4duuzq7jjaz" />
}, {
  name: 'Invesko',
  svg: <img src="https://cdn.magicpatterns.com/uploads/gzpUxwdPjU3oPvmSCR2wMS/Group_18.svg" alt="Invesko" style={{
    height: 28,
    width: 'auto',
    display: 'block'
  }} data-id="element-120" data-mp="1sc3yb0ul7kzq" />
}, {
  name: 'RIFE',
  svg: <img src="https://cdn.magicpatterns.com/uploads/pNdj8CivscfqTY54NqSnGq/Group_96.svg" alt="RIFE" style={{
    height: 32,
    width: 'auto',
    display: 'block'
  }} data-id="element-121" data-mp="14fq5ot2c89s5" />
}, {
  name: 'Mind What Matters',
  svg: <img src="https://cdn.magicpatterns.com/uploads/x5SPAFmdV3DPBmszavcJvt/Group_1948754876.svg" alt="Mind What Matters" style={{
    height: 36,
    width: 'auto',
    display: 'block'
  }} data-id="element-122" data-mp="33sat7m3v8moa" />
}, {
  name: 'Brand 5',
  svg: <img src="https://cdn.magicpatterns.com/uploads/fMN75D6PtyKVVy8NNViyEw/Group_1948754968.svg" alt="Brand 5" style={{
    height: 32,
    width: 'auto',
    display: 'block'
  }} data-id="element-123" data-mp="2scz95gn0d3wj" />
}, {
  name: 'xchange',
  svg: <img src="https://cdn.magicpatterns.com/uploads/71Lro4NoAkxFCFdAzLk97j/Group_1948755017.svg" alt="xchange" style={{
    height: 32,
    width: 'auto',
    display: 'block'
  }} data-id="element-124" data-mp="3l3hcmirn97ah" />
}, {
  name: 'kinhub',
  svg: <img src="https://cdn.magicpatterns.com/uploads/awHy4mzbEbrupQxFR7WVZ7/Group.svg" alt="kinhub" style={{
    height: 36,
    width: 'auto',
    display: 'block'
  }} data-id="element-125" data-mp="36anwcv4jsn4r" />
}, {
  name: 'Become',
  svg: <img src="https://cdn.magicpatterns.com/uploads/kVT8X9YqpMLqs5nSao5PAa/image_268.svg" alt="Become" style={{
    height: 32,
    width: 'auto',
    display: 'block'
  }} data-id="element-126" data-mp="b68rh2gfdu5l" />
}, {
  name: 'Humble',
  svg: <img src="https://cdn.magicpatterns.com/uploads/1PnzhdmiL9DA55f4hbSHHS/LOGOWHITE-SVG_2.svg" alt="Humble" style={{
    height: 40,
    width: 'auto',
    display: 'block',
    filter: 'invert(1)'
  }} data-id="element-127" data-mp="p5gt02029ecy" />
}, {
  name: 'Opus',
  svg: <img src="https://cdn.magicpatterns.com/uploads/jU8onH1hNM592RcetgLpfp/opus_logo.svg" alt="Opus" style={{
    height: 28,
    width: 'auto',
    display: 'block'
  }} data-id="element-128" data-mp="12x53k0unvurt" />
}, {
  name: 'Firetree',
  svg: <img src="https://cdn.magicpatterns.com/uploads/iW7ZsTjF8XaurgwEoW6hNX/logo-black.jpg" alt="Firetree" style={{
    height: 32,
    width: 'auto',
    display: 'block'
  }} data-id="element-129" data-mp="1hsbdv00sifqy" />
}, {
  name: 'Narra Health',
  svg: <img src="https://cdn.magicpatterns.com/uploads/jtZUQzZvVbm7GxG76gKHTs/pasted-image.svg" alt="Narra Health" style={{
    height: 28,
    width: 'auto',
    display: 'block'
  }} data-id="element-130" data-mp="2oasvguciaozk" />
}, {
  name: 'Figma',
  svg: <svg width="28" height="28" viewBox="0 0 38 57" fill="none" data-id="element-131" data-mp="1qmsju41ogaeo">
        <path d="M19 28.5C19 25.9804 20.0009 23.5641 21.7825 21.7825C23.5641 20.0009 25.9804 19 28.5 19C31.0196 19 33.4359 20.0009 35.2175 21.7825C36.9991 23.5641 38 25.9804 38 28.5C38 31.0196 36.9991 33.4359 35.2175 35.2175C33.4359 36.9991 31.0196 38 28.5 38C25.9804 38 23.5641 36.9991 21.7825 35.2175C20.0009 33.4359 19 31.0196 19 28.5Z" fill={LOGO_COLOR} data-id="element-132" data-mp="1qon89sc5qkni" />
        <path d="M0 47.5C0 44.9804 1.00089 42.5641 2.78249 40.7825C4.56408 39.0009 6.98044 38 9.5 38H19V47.5C19 50.0196 17.9991 52.4359 16.2175 54.2175C14.4359 55.9991 12.0196 57 9.5 57C6.98044 57 4.56408 55.9991 2.78249 54.2175C1.00089 52.4359 0 50.0196 0 47.5Z" fill={LOGO_COLOR} data-id="element-133" data-mp="3bzlwv6bwdp1k" />
        <path d="M19 0V19H28.5C31.0196 19 33.4359 17.9991 35.2175 16.2175C36.9991 14.4359 38 12.0196 38 9.5C38 6.98044 36.9991 4.56408 35.2175 2.78249C33.4359 1.00089 31.0196 0 28.5 0H19Z" fill={LOGO_COLOR} data-id="element-134" data-mp="39nbempcvhb7o" />
        <path d="M0 9.5C0 12.0196 1.00089 14.4359 2.78249 16.2175C4.56408 17.9991 6.98044 19 9.5 19H19V0H9.5C6.98044 0 4.56408 1.00089 2.78249 2.78249C1.00089 4.56408 0 6.98044 0 9.5Z" fill={LOGO_COLOR} data-id="element-135" data-mp="b44gmsneaeph" />
        <path d="M0 28.5C0 31.0196 1.00089 33.4359 2.78249 35.2175C4.56408 36.9991 6.98044 38 9.5 38H19V19H9.5C6.98044 19 4.56408 20.0009 2.78249 21.7825C1.00089 23.5641 0 25.9804 0 28.5Z" fill={LOGO_COLOR} data-id="element-136" data-mp="1cgsb0m4xds8" />
      </svg>
}, {
  name: 'Framer',
  svg: <svg width="28" height="28" viewBox="0 0 14 21" fill="none" data-id="element-137" data-mp="13orqzpwre66m">
        <path d="M0 14H7L14 21H0V14ZM0 7H14L7 14H0V7ZM14 0H0V7H14L7 0Z" fill={LOGO_COLOR} data-id="element-138" data-mp="yqggre2jxtnv" />
      </svg>
}, {
  name: 'Webflow',
  svg: <svg width="36" height="24" viewBox="0 0 90 60" fill="none" data-id="element-139" data-mp="pqa7639z5dtz">
        <path d="M66.7 0S55.5 33.4 54.9 35.4c-.1-.9-6.3-35.4-6.3-35.4H36.1S24.9 33.4 24.3 35.4C24.2 34.5 18 0 18 0H0l12 60h14.2L39 23.2 51.8 60H66L90 0H66.7z" fill={LOGO_COLOR} data-id="element-140" data-mp="pq0f8a5dcwln" />
      </svg>
}, {
  name: 'Vercel',
  svg: <svg width="28" height="24" viewBox="0 0 1155 1000" fill="none" data-id="element-141" data-mp="b0m715dj4l7u">
        <path d="M577.344 0L1154.69 1000H0L577.344 0Z" fill={LOGO_COLOR} data-id="element-142" data-mp="6zse1xl8cg83" />
      </svg>
}, {
  name: 'GitHub',
  svg: <svg width="26" height="26" viewBox="0 0 98 96" fill="none" data-id="element-143" data-mp="1znx66getjhc">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" fill={LOGO_COLOR} data-id="element-144" data-mp="3t7a9enh72u9t" />
      </svg>
}, {
  name: 'Stripe',
  svg: <svg width="34" height="14" viewBox="0 0 60 25" fill="none" data-id="element-145" data-mp="1b929vj6mujba">
        <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a14.14 14.14 0 0 1-4.55.83c-4.08 0-6.84-2.58-6.84-7.43 0-4.3 2.47-7.43 6.32-7.43 3.85 0 5.93 3.13 5.93 7.43 0 .41-.03 1.28-.05 1.68zm-5.92-5.15c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20c-1.58 0-2.51-.7-3.16-1.19l-.02 5.36-4.38.93V5.76h3.86l.14 1.19c.65-.76 1.77-1.55 3.56-1.55 3.21 0 6.18 2.87 6.18 7.4C47.13 17.26 44.2 20 40.95 20zm-.94-10.49c-1.07 0-1.71.41-2.14.93l.02 7.28c.4.49 1.03.93 2.12.93 1.68 0 2.81-1.85 2.81-4.57 0-2.69-1.15-4.57-2.81-4.57zM28.24 5.76h4.4V19.7h-4.4V5.76zm0-4.7 4.4-.94v3.56l-4.4.94V1.06zM23.76 8.3l-.28-2.54h-3.8V19.7h4.38V11.6c1.03-1.35 2.77-1.1 3.31-.93V5.76c-.56-.19-2.62-.52-3.61 2.54zM14.64 5.76l-4.38.94V19.7h4.38V5.76zm0-4.7L10.26 0v3.56l4.38.94V1.06zM7.14 7.5C5.95 7.03 4.41 6.5 2.7 6.5c-2.06 0-3.3 1.01-3.3 2.48 0 1.65 1.2 2.3 3.23 3.09 2.58.99 3.93 2.27 3.93 4.64 0 3.07-2.4 4.29-5.3 4.29C-.06 21 -1.3 20.5-2.4 20v-3.7c1.1.58 2.5 1.1 3.9 1.1 1.5 0 2.5-.52 2.5-1.8 0-1.2-.9-1.87-2.8-2.65C-1.2 11.9-2.7 10.5-2.7 8.1c0-2.9 2.2-4.6 5.5-4.6 1.5 0 2.8.37 3.84.8V7.5z" fill={LOGO_COLOR} data-id="element-146" data-mp="1b77qwaex919u" />
      </svg>
}, {
  name: 'Shopify',
  svg: <svg width="26" height="30" viewBox="0 0 109 124" fill="none" data-id="element-147" data-mp="1by5jvry4cxnk">
        <path d="M74.7 14.8s-.3 0-.8.2c-.5-1.4-1.3-2.7-2.3-3.8-3.3-3.6-8.1-5.4-12.8-5.4-.3 0-.7 0-1 .1-.2-.2-.3-.4-.5-.6-2-2.2-4.8-3.3-8.1-3.2-6.3.2-12.5 4.7-17.6 12.7-3.5 5.6-6.2 12.6-7 18l-12 3.7c-3.5 1.1-3.6 1.2-4.1 4.5C8.1 43.4 0 105.8 0 105.8l75.6 13.1V14.6c-.3.1-.6.2-.9.2zm-17.2 5.3c-4.2 1.3-8.8 2.7-13.4 4.1.9-3.4 2.6-6.8 4.7-9.1 1.1-1.2 2.6-2.4 4.4-3.1 1.8 3.8 2.3 8.1 2.3 8.1zm-8.4-11.4c1.5 0 2.7.3 3.8.9-1.7.9-3.3 2.2-4.7 3.8-3.8 4.1-6.7 10.5-7.6 16.6l-10.1 3.1c1.9-9.1 9.3-24.1 18.6-24.4zm4.7 53.6c.4 6.4-17.4 7.8-18 1.2-.6-6.6 4.1-9.8 9.1-9.8 1.4 0 2.8.3 4.1.8l1.1-4.5c-1.6-.6-3.3-.9-5.1-.9-8.3 0-14.8 5.6-14.8 14.4 0 9.4 7.4 12.4 14.1 12.4 7.6 0 12.9-4.1 12.9-10.3 0-.1 0-.2 0-.3h-3.4zm11.8-42.2c-.1 0-.2 0-.3 0v-.4c0-3.9-.5-7-1.5-9.5 3.7.5 6.2 4.6 7.3 9.9l-5.5 0z" fill={LOGO_COLOR} data-id="element-148" data-mp="1bzys3bodrpxo" />
        <path d="M74.7 14.8l-.9.2V119l34.3-8.6S93.5 19.2 93.4 18.5c-.1-.7-.7-1.1-1.3-1.2l-12.2-1.9c-.5-3.1-2.5-5.4-5.2-5.4v4.8z" fill={LOGO_COLOR} data-id="element-149" data-mp="b57hydysly3d" />
      </svg>
}, {
  name: 'React',
  svg: <svg width="30" height="28" viewBox="0 0 841.9 595.3" fill="none" data-id="element-150" data-mp="24sv3a66kaxr">
        <g fill={LOGO_COLOR} data-id="element-151" data-mp="ptc1fk5o6dn7">
          <path d="M666.3 296.5c0-32.5-40.7-63.3-103.1-82.4 14.4-63.6 8-114.2-20.2-130.4-6.5-3.8-14.1-5.6-22.4-5.6v22.3c4.6 0 8.3.9 11.4 2.6 13.6 7.8 19.5 37.5 14.9 75.7-1.1 9.4-2.9 19.3-5.1 29.4-19.6-4.8-41-8.5-63.5-10.9-13.5-18.5-27.5-35.3-41.6-50 32.6-30.3 63.2-46.9 84-46.9V78c-27.5 0-63.5 19.6-99.9 53.6-36.4-33.8-72.4-53.2-99.9-53.2v22.3c20.7 0 51.4 16.5 84 46.6-14 14.7-28 31.4-41.3 49.9-22.6 2.4-44 6.1-63.6 11-2.3-10-4-19.7-5.2-29-4.7-38.2 1.1-67.9 14.6-75.8 3-1.8 6.9-2.6 11.5-2.6V78.5c-8.4 0-16 1.8-22.6 5.6-28.1 16.2-34.4 66.7-19.9 130.1-62.2 19.2-102.7 49.9-102.7 82.3 0 32.5 40.7 63.3 103.1 82.4-14.4 63.6-8 114.2 20.2 130.4 6.5 3.8 14.1 5.6 22.5 5.6 27.5 0 63.5-19.6 99.9-53.6 36.4 33.8 72.4 53.2 99.9 53.2 8.4 0 16-1.8 22.6-5.6 28.1-16.2 34.4-66.7 19.9-130.1 62-19.1 102.5-49.9 102.5-82.3zm-130.2-66.7c-3.7 12.9-8.3 26.2-13.5 39.5-4.1-8-8.4-16-13.1-24-4.6-8-9.5-15.8-14.4-23.4 14.2 2.1 27.9 4.7 41 7.9zm-45.8 106.5c-7.8 13.5-15.8 26.3-24.1 38.2-14.9 1.3-30 2-45.2 2-15.1 0-30.2-.7-45-1.9-8.3-11.9-16.4-24.6-24.2-38-7.6-13.1-14.5-26.4-20.8-39.8 6.2-13.4 13.2-26.8 20.7-39.9 7.8-13.5 15.8-26.3 24.1-38.2 14.9-1.3 30-2 45.2-2 15.1 0 30.2.7 45 1.9 8.3 11.9 16.4 24.6 24.2 38 7.6 13.1 14.5 26.4 20.8 39.8-6.3 13.4-13.2 26.8-20.7 39.9zm32.3-13c5.4 13.4 10 26.8 13.8 39.8-13.1 3.2-26.9 5.9-41.2 8 4.9-7.7 9.8-15.6 14.4-23.7 4.6-8 8.9-16.1 13-24.1zM421.2 430c-9.3-9.6-18.6-20.3-27.8-32 9 .4 18.2.7 27.5.7 9.4 0 18.7-.2 27.8-.7-9 11.7-18.3 22.4-27.5 32zm-74.4-58.9c-14.2-2.1-27.9-4.7-41-7.9 3.7-12.9 8.3-26.2 13.5-39.5 4.1 8 8.4 16 13.1 24 4.7 8 9.5 15.8 14.4 23.4zM420.7 163c9.3 9.6 18.6 20.3 27.8 32-9-.4-18.2-.7-27.5-.7-9.4 0-18.7.2-27.8.7 9-11.7 18.3-22.4 27.5-32zm-74 58.9c-4.9 7.7-9.8 15.6-14.4 23.7-4.6 8-8.9 16.1-13 24.1-5.4-13.4-10-26.8-13.8-39.8 13.1-3.1 26.9-5.8 41.2-7.9zm-90.5 125.2c-35.4-15.1-58.3-34.9-58.3-50.6 0-15.7 22.9-35.6 58.3-50.6 8.6-3.7 18-7 27.7-10.1 5.7 19.6 13.2 40 22.5 60.9-9.2 20.8-16.6 41.1-22.2 60.6-9.9-3.1-19.3-6.5-28-10.2zM310 490c-13.6-7.8-19.5-37.5-14.9-75.7 1.1-9.4 2.9-19.3 5.1-29.4 19.6 4.8 41 8.5 63.5 10.9 13.5 18.5 27.5 35.3 41.6 50-32.6 30.3-63.2 46.9-84 46.9-4.5-.1-8.3-1-11.3-2.7zm237.2-76.2c4.7 38.2-1.1 67.9-14.6 75.8-3 1.8-6.9 2.6-11.5 2.6-20.7 0-51.4-16.5-84-46.6 14-14.7 28-31.4 41.3-49.9 22.6-2.4 44-6.1 63.6-11 2.3 10.1 4.1 19.8 5.2 29.1zm38.5-66.7c-8.6 3.7-18 7-27.7 10.1-5.7-19.6-13.2-40-22.5-60.9 9.2-20.8 16.6-41.1 22.2-60.6 9.9 3.1 19.3 6.5 28.1 10.2 35.4 15.1 58.3 34.9 58.3 50.6-.1 15.7-23 35.6-58.4 50.6zM320.8 78.4z" data-id="element-152" data-mp="pu3xew36ide0" />
          <circle cx="420.9" cy="296.5" r="45.7" data-id="element-153" data-mp="1uxd38543j6lq" />
        </g>
      </svg>
}, {
  name: 'Tailwind',
  svg: <svg width="34" height="20" viewBox="0 0 54 33" fill="none" data-id="element-154" data-mp="1nedkm1oglj3f">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M27 0C19.8 0 15.3 3.6 13.5 10.8c2.7-3.6 5.85-4.95 9.45-4.05 2.054.514 3.522 2.004 5.147 3.653C30.744 13.09 33.808 16.2 40.5 16.2c7.2 0 11.7-3.6 13.5-10.8-2.7 3.6-5.85 4.95-9.45 4.05-2.054-.514-3.522-2.004-5.147-3.653C36.756 3.11 33.692 0 27 0zM13.5 16.2C6.3 16.2 1.8 19.8 0 27c2.7-3.6 5.85-4.95 9.45-4.05 2.054.514 3.522 2.004 5.147 3.653C17.244 29.29 20.308 32.4 27 32.4c7.2 0 11.7-3.6 13.5-10.8-2.7 3.6-5.85 4.95-9.45 4.05-2.054-.514-3.522-2.004-5.147-3.653C23.256 19.31 20.192 16.2 13.5 16.2z" fill={LOGO_COLOR} data-id="element-155" data-mp="1ih3pc5ee2d7o" />
      </svg>
}, {
  name: 'TypeScript',
  svg: <svg width="26" height="26" viewBox="0 0 400 400" fill="none" data-id="element-156" data-mp="lq2t9701czqh">
        <rect width="400" height="400" rx="50" fill={LOGO_COLOR} data-id="element-157" data-mp="gqq6em95zsnw" />
        <path d="M87.7 200.7V217h52v148h36.9V217h52v-16c0-9 0-16.3-.4-16.5 0-.3-31.7-.4-70.2-.4l-70 .3v16.3zM321.4 184c10.2 2.4 18 7 25 14.3 3.7 4 9.2 11 9.6 12.8 0 .5-17.3 12.3-27.8 18.8-.4.3-2-1.4-3.6-4-5.2-7.4-10.5-10.6-18.8-11.2-12.1-.8-20 5.5-20 16 0 3.2.5 5 1.8 7.6 2.7 5.5 7.7 8.8 23.2 15.6 28.6 12.3 41 20.4 48.5 32 8.5 13 10.4 33.4 4.7 48.7-6.4 16.7-22.2 28-44.3 31.6-7 1.2-23.4 1-30.9-.3-16.2-2.8-31.6-11-41.1-21.8-3.7-4.2-10.9-15.4-10.5-16.2.2-.3 1.8-1.3 3.6-2.3 1.8-1 8.7-5 15.2-8.7l11.8-6.8 2.4 3.5c3.3 5.2 10.7 12.2 15.1 14.6 12.7 6.7 30.1 5.7 38.7-2 3.7-3.4 5.3-6.9 5.3-12 0-4.6-.7-6.7-3-10.2-3.2-4.4-9.6-8-27.6-16-20.7-8.9-29.5-14.4-37.7-23-4.7-5.2-9-13.3-10.8-20.1-1.5-5.8-2-19.8-.7-25.5 4.2-19.7 19.3-33.5 40.8-38.1 7-1.5 23.2-1.2 30.1.6z" fill="white" data-id="element-158" data-mp="gri3y2mbo0gt" />
      </svg>
}];
// Duplicate for seamless infinite loop
const CAROUSEL_ITEMS = [...LOGOS, ...LOGOS];
function LogoCarousel() {
  return <div className="w-full overflow-hidden py-6 mb-12" style={{
    borderTop: '1px solid var(--border)',
    borderBottom: '1px solid var(--border)',
    background: 'transparent'
  }} data-id="element-159" data-mp="3nh4pahierrs6">
      <div style={{
      display: 'flex',
      width: 'max-content',
      animation: 'marquee 40s linear infinite',
      willChange: 'transform'
    }} className="logo-carousel" data-id="element-160" data-mp="3sn1jzcp4c14o">
        {CAROUSEL_ITEMS.map((logo, i) => <div key={i} className="flex items-center justify-center" style={{
        padding: '0 40px',
        opacity: 0.7,
        flexShrink: 0
      }} title={logo.name} data-id="element-161" data-mp="k66o4h6qnh03" data-mpi={i}>
            {logo.svg}
          </div>)}
      </div>
    </div>;
}
interface ServicePill {
  label: string;
  icon: React.ReactNode;
}
const discoveryPills: ServicePill[] = [{
  label: 'Product Discovery',
  icon: <PackageIcon size={13} data-id="element-162" data-mp="2v0b4v75humob" />
}, {
  label: 'UX/UI',
  icon: <MousePointer2Icon size={13} data-id="element-163" data-mp="2qyh839yf62tf" />
}, {
  label: 'User Research',
  icon: <SearchIcon size={13} data-id="element-164" data-mp="txbqu05n62z1" />
}, {
  label: 'Roadmapping',
  icon: <MapIcon size={13} data-id="element-165" data-mp="yv3euj3614xy" />
}, {
  label: 'Design Systems',
  icon: <ComponentIcon size={13} data-id="element-166" data-mp="yxfwsw8xbfb0" />
}, {
  label: 'Branding',
  icon: <PaletteIcon size={13} data-id="element-167" data-mp="13tn68z0j38xp" />
}];
const prototypingPills: ServicePill[] = [{
  label: 'No-Code POC',
  icon: <ZapIcon size={13} data-id="element-168" data-mp="18uubaqbadu0o" />
}, {
  label: 'Alpha & Beta',
  icon: <FlaskConicalIcon size={13} data-id="element-169" data-mp="1cu35fwa8ntwr" />
}, {
  label: 'Mobile Apps',
  icon: <SmartphoneIcon size={13} data-id="element-170" data-mp="1htxstput21d2" />
}, {
  label: 'Web Apps',
  icon: <MonitorIcon size={13} data-id="element-171" data-mp="1hruvslv8j7yu" />
}, {
  label: 'AI Integrations',
  icon: <BotIcon size={13} data-id="element-172" data-mp="1mlfwilo7qg1q" />
}, {
  label: 'E-Commerce',
  icon: <ShoppingBagIcon size={13} data-id="element-173" data-mp="1rkaddi2mduj7" />
}];
const engineeringPills: ServicePill[] = [{
  label: 'Product Design',
  icon: <PencilRulerIcon size={13} data-id="element-174" data-mp="1wketv83j2p9f" />
}, {
  label: 'Frontend Dev',
  icon: <CodeIcon size={13} data-id="element-175" data-mp="20jxhwcqnbggh" />
}, {
  label: 'Backend & APIs',
  icon: <ServerIcon size={13} data-id="element-176" data-mp="3l2rjdn5pdwwq" />
}, {
  label: 'Web Design',
  icon: <LayoutTemplateIcon size={13} data-id="element-177" data-mp="3l42njo3296od" />
}, {
  label: 'QA & Launch',
  icon: <CheckCircleIcon size={13} data-id="element-178" data-mp="3h0d0x45y4z5z" />
}, {
  label: 'Ongoing Support',
  icon: <HeadphonesIcon size={13} data-id="element-179" data-mp="3c19ubfabikvw" />
}];
function PillGrid({
  pills
}: {
  pills: ServicePill[];
}) {
  return <>
      <div className="w-full mb-4 md:mb-6" style={{
      height: '1px',
      background: '#E8E8E8'
    }} data-id="element-180" data-mp="3749xzw39swhz" />
      <div className="grid grid-cols-2 gap-1.5" data-id="element-181" data-mp="8n5scq2f457o">
        {pills.map(pill => <div key={pill.label} className="flex items-center gap-2 px-3 py-2 rounded-full text-xs overflow-hidden" style={{
        background: '#EBEBEB',
        color: 'var(--fg)'
      }} data-id="element-182" data-mp="3oclvhljteh0">
            <span style={{
          color: 'var(--muted)',
          flexShrink: 0
        }} data-id="element-183" data-mp="rcvu5x4llr93">
              {pill.icon}
            </span>
            <span className="truncate whitespace-nowrap" data-id="element-184" data-mp="3r2ot5f8l1q5x">{pill.label}</span>
          </div>)}
      </div>
    </>;
}
export function Services() {
  const headerRef = useReveal();
  const card1Ref = useReveal(0);
  const card2Ref = useReveal(80);
  const card3Ref = useReveal(160);
  return <section id="services" className="w-full pt-8 md:pt-16 pb-24 md:pb-32" data-id="element-185" data-mp="3m3c89019ut5z">
      <div className="content-wrap px-6 md:px-12" data-id="element-186" data-mp="do0u7zk74s5a">
        {/* Section header */}
        <div ref={headerRef} className="reveal flex items-end justify-between mb-12 pt-8" style={{
        borderColor: 'var(--border)'
      }} data-id="element-187" data-mp="dlxyr009y6t6">
          <div data-id="element-188" data-mp="4pggzdxenaog">
            <p className="text-xs tracking-[0.2em] uppercase mb-3 font-[300]" style={{
            color: 'var(--muted)'
          }} data-id="element-189" data-mp="9mfbvyow7lnp">
              What we do
            </p>
            <h2 className="font-[250] text-4xl md:text-6xl tracking-[-0.03em]" style={{
            color: 'var(--fg)'
          }} data-id="element-190" data-mp="2ud7h8ij0b7vs">
              Services
            </h2>
          </div>
        </div>

        {/* Three-column service cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-191" data-mp="2vud2if9cu0mw">
          {/* Discovery & Strategy */}
          <div ref={card1Ref} className="reveal rounded-2xl p-6 flex flex-col transition-all duration-300" style={{
          background: '#F4F4F4'
        }} onMouseEnter={e => {
          e.currentTarget.style.background = '#EEEEEE';
          e.currentTarget.style.transform = 'scale(1.015)';
        }} onMouseLeave={e => {
          e.currentTarget.style.background = '#F4F4F4';
          e.currentTarget.style.transform = 'scale(1)';
        }} data-id="element-192" data-mp="2qugzojce8f8x">
            <div className="w-full rounded-xl overflow-hidden mb-4 md:mb-6" data-id="element-193" data-mp="1kclk2oc6ig9z">
              <img src="https://cdn.magicpatterns.com/uploads/7kyFz592eHg8CkLqWbcCrM/lego.png" alt="Design building blocks illustration" draggable={false} style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              userSelect: 'none'
            }} data-id="element-194" data-mp="1ff21xdcd1jcr" />
            </div>
            <p className="text-xs tracking-[0.18em] uppercase mb-3 md:mb-5 font-[300]" style={{
            color: 'var(--muted)'
          }} data-id="element-195" data-mp="uldhwmcfueoy">
              System before the surface
            </p>
            <h3 className="font-medium text-xl tracking-tight leading-snug mb-2 md:mb-4" style={{
            color: 'var(--fg)'
          }} data-id="element-196" data-mp="14i053hs8lhu5">
              Discovery & Strategy
            </h3>
            <p className="text-sm leading-relaxed mb-4 md:mb-6 text-[#4E4E4E]" data-id="element-197" data-mp="31cw6a4aiv2s0">
              We run focused discovery sprints to define the real problem, map
              workflows, and prototype concepts fast, before you overspend on
              design or engineering.
            </p>
            <PillGrid pills={discoveryPills} data-id="element-198" data-mp="174scbcwpl1n4" />
          </div>

          {/* Rapid Prototyping */}
          <div ref={card2Ref} className="reveal rounded-2xl p-6 flex flex-col transition-all duration-300" style={{
          background: '#F4F4F4'
        }} onMouseEnter={e => {
          e.currentTarget.style.background = '#EEEEEE';
          e.currentTarget.style.transform = 'scale(1.015)';
        }} onMouseLeave={e => {
          e.currentTarget.style.background = '#F4F4F4';
          e.currentTarget.style.transform = 'scale(1)';
        }} data-id="element-199" data-mp="1c3c3psu2n5mv">
            <div className="w-full rounded-xl overflow-hidden mb-4 md:mb-6" data-id="element-200" data-mp="2ikh0g7lp86pi">
              <img src="https://cdn.magicpatterns.com/uploads/k2vq6tM3ir22CRVYkJA4M2/dev_services.png" alt="Development services illustration" draggable={false} style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              userSelect: 'none'
            }} data-id="element-201" data-mp="2nhfwivoxanqc" />
            </div>
            <p className="text-xs tracking-[0.18em] uppercase mb-3 md:mb-5 font-[300]" style={{
            color: 'var(--muted)'
          }} data-id="element-202" data-mp="34smi9z06bkfp">
              Validation by design
            </p>
            <h3 className="font-medium text-xl tracking-tight leading-snug mb-2 md:mb-4" style={{
            color: 'var(--fg)'
          }} data-id="element-203" data-mp="2v0p1mcqec09l">
              Rapid Prototyping
            </h3>
            <p className="text-sm leading-relaxed mb-4 md:mb-6 text-[#4E4E4E]" data-id="element-204" data-mp="3is4aypglbhuc">
              We've seen how AI-generated high-fidelity prototypes cut approval
              cycles by up to 90%. You see the real product early, validate it
              quickly, and build only what works.
            </p>
            <PillGrid pills={prototypingPills} data-id="element-205" data-mp="3w2977usfxl2" />
          </div>

          {/* Design & Engineering */}
          <div ref={card3Ref} className="reveal rounded-2xl p-6 flex flex-col transition-all duration-300" style={{
          background: '#F4F4F4'
        }} onMouseEnter={e => {
          e.currentTarget.style.background = '#EEEEEE';
          e.currentTarget.style.transform = 'scale(1.015)';
        }} onMouseLeave={e => {
          e.currentTarget.style.background = '#F4F4F4';
          e.currentTarget.style.transform = 'scale(1)';
        }} data-id="element-206" data-mp="8vewtuthh8op">
            <div className="w-full rounded-xl overflow-hidden mb-4 md:mb-6" data-id="element-207" data-mp="wid9ehehfwhc">
              <img src="https://cdn.magicpatterns.com/uploads/2eB2F9djTwpm7w4BVhcE6e/record_final.png" alt="Strategy and growth illustration" draggable={false} loading="lazy" style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              background: 'transparent',
              mixBlendMode: 'multiply',
              userSelect: 'none'
            }} data-id="element-208" data-mp="11kcblnyhkncp" />
            </div>
            <p className="text-xs tracking-[0.18em] uppercase mb-3 md:mb-5 font-[300]" style={{
            color: 'var(--muted)'
          }} data-id="element-209" data-mp="1ae0ouux91t1a">
              Prototype to production
            </p>
            <h3 className="font-medium text-xl tracking-tight leading-snug mb-2 md:mb-4" style={{
            color: 'var(--fg)'
          }} data-id="element-210" data-mp="1kdj5p2hv4yp4">
              Design & Engineering
            </h3>
            <p className="text-sm leading-relaxed mb-4 md:mb-6 text-[#4E4E4E]" data-id="element-211" data-mp="338ta2sdtti2d">
              One team handles design, frontend, backend, and launch. We stay
              involved end to end so the product that ships matches the product
              that was planned.
            </p>
            <PillGrid pills={engineeringPills} data-id="element-212" data-mp="2ya8bexe836g1" />
          </div>
        </div>
      </div>
    </section>;
}